import React from 'react';
import {
    Checkmark24,
    Copy24,
    DataShare32,
    LicenseDraft24,
    Link24,
    OverflowMenuVertical16,
    TextLinkAnalysis24,
} from '@carbon/icons-react';
import { sendFormStatusDownstream } from '../../apiCalls/dashboard/sendFormStatusDownstream';
import Button from '@mercell/button-react';
import { DropdownMenu, DropdownMenuItem } from '@mercell/dropdown-menu-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FormStatus } from '../../types/generated/formMenuResult';
import { getOtpUrlFromApi } from '../../authorization/getAuthFromApi';
import { formOptions } from '../../types/enums/formOptions';
import {
    publicationTaskStatusName,
} from './publicationTaskStatus';

interface TableDropDownProps {
    cell: any;
    t: any;
    handleDropDownShow: (e: any, cell: any) => void;
    copyToClipboard: (text: string) => void;
    openModal: (formID: string) => void;
    openModalBackToDispatched: (formID: string) => void;
    openModalPublicationStop: (formID: string) => void;

    viewAuditLog: (
        formId: string,
        tenderId: string,
        tenderingSystem: string
    ) => void;

    viewStatusLog: (
        formId: string,
        tenderId: string,
        tenderingSystem: string
    ) => void;

    viewPublicationLog: (formId: string) => void;
    viewValidationFull: (tenderData: any) => void;
}

const TableDropDown = (props: TableDropDownProps) => {
    const { mef1941 } = useFlags();

    const {
        cell,
        t,
        handleDropDownShow,
        copyToClipboard,
        openModal,
        openModalBackToDispatched,
        openModalPublicationStop,
    } = props;
    const buttonRef = React.useRef<HTMLButtonElement>(null);

    const shouldDisabled = (original: any): boolean | undefined => {
        const result =
            original.status === FormStatus.Error ||
            original.status === FormStatus.ReadyForTranslation;

        if (mef1941) {
            return !(
                result ||
                (original.portalName === 'UHM' &&
                    original.portalStatus ===
                        publicationTaskStatusName.failed)
            );
        }

        return !result;
    };

    return (
        <>
            <Button
                scheme="icon"
                className="rt-tr-group focus:ring-offset-0 text-minsk focus:bg-transparent"
                ref={buttonRef}
                style={{ boxShadow: 'none' }}
                iconSettings={{
                    Icon: OverflowMenuVertical16,
                }}
                onClick={(e) => handleDropDownShow(e, cell)}
                data-test="array-button"
            />
            {cell.row.original.showDropDown && (
                <DropdownMenu
                    id="dashboard-table-dropdown-menu"
                    anchorRef={buttonRef}
                    closeOnClick
                    onClose={(e) => handleDropDownShow(e, cell)}
                    popperOptions={{ placement: 'left-start' }}
                    className="p-4 !min-w-[300px]"
                >
                    <div className="flex items-center justify-center px-2">
                        <LicenseDraft24 className="text-minsk" />
                        <DropdownMenuItem
                            className="hover:bg-transparent hover:text-minsk focus:ring-offset-0  active:bg-transparent"
                            data-test="array-item-edit-button"
                            onClick={() => openModal(cell.row.original.formId)}
                            disabled={
                                !(
                                    cell.row.original.status ===
                                        FormStatus.ReadyForPublication ||
                                    cell.row.original.status ===
                                        FormStatus.Error ||
                                    cell.row.original.status ===
                                        FormStatus.ReadyForTranslation ||
                                    cell.row.original.status ===
                                        FormStatus.Deleted ||
                                    cell.row.original.status ===
                                        FormStatus.PublicationStopped
                                )
                            }
                        >
                            {t('dashboard.table-dropdown:returnStatusToDraft')}
                        </DropdownMenuItem>
                    </div>
                    <div className="flex items-center justify-center px-2">
                        <LicenseDraft24 className="text-minsk" />
                        <DropdownMenuItem
                            className="hover:bg-transparent hover:text-minsk focus:ring-offset-0  active:bg-transparent"
                            data-test="array-item-edit-button"
                            onClick={() =>
                                openModalBackToDispatched(
                                    cell.row.original.formId
                                )
                            }
                            disabled={shouldDisabled(cell.row.original)}
                        >
                            {t('dashboard.table-dropdown:returnStatusToDispatchedForPublication')}
                        </DropdownMenuItem>
                    </div>
                    <div className="flex items-center justify-center px-2">
                        <TextLinkAnalysis24 className="text-minsk" />
                        <DropdownMenuItem
                            className="hover:bg-transparent hover:text-minsk focus:ring-offset-0  active:bg-transparent"
                            data-test="array-item-edit-button"
                            onClick={() =>
                                props.viewAuditLog(
                                    cell.row.original.formId,
                                    cell.row.original.tenderId,
                                    props?.cell.row.original.tenderingSystem
                                )
                            }
                        >
                            {t('form-content.logs:auditLog')}
                        </DropdownMenuItem>
                    </div>
                    <div className="flex items-center justify-center px-2">
                        <TextLinkAnalysis24 className="text-minsk" />
                        <DropdownMenuItem
                            className="hover:bg-transparent hover:text-minsk focus:ring-offset-0  active:bg-transparent"
                            data-test="array-item-edit-button"
                            onClick={() =>
                                props.viewStatusLog(
                                    cell.row.original.formId,
                                    cell.row.original.tenderId,
                                    props?.cell.row.original.tenderingSystem
                                )
                            }
                        >
                            {t('form-content.logs:statusLog')}
                        </DropdownMenuItem>
                    </div>

                    <div className="flex items-center justify-center px-2">
                        <TextLinkAnalysis24 className="text-minsk" />
                        <DropdownMenuItem
                            className="hover:bg-transparent hover:text-minsk focus:ring-offset-0  active:bg-transparent"
                            data-test="array-item-edit-button"
                            onClick={() =>
                                props.viewPublicationLog(
                                    cell.row.original.formId
                                )
                            }
                        >
                            {t('form-content.logs:publicationLog')}
                        </DropdownMenuItem>
                    </div>
                    <div className="flex items-center justify-center px-2">
                        <DataShare32 className="text-minsk" />
                        <DropdownMenuItem
                            className="hover:bg-transparent hover:text-minsk focus:ring-offset-0  active:bg-transparent"
                            data-test="send-status-downstream-button"
                            onClick={() =>
                                sendFormStatusDownstream(cell.row.original.formId, {
                                    onPendingText: t('toast-content.toast:saving'),
                                    onSuccessText: t(
                                        'toast-content.status-downstream:sendSuccess'
                                    ),
                                })
                            }
                        >
                            {t('dashboard.table-dropdown:sendStatusDownstream')}
                        </DropdownMenuItem>
                    </div>
                    <div className="flex items-center justify-center px-2">
                        <Link24 className="text-minsk" />
                        <DropdownMenuItem
                            className="hover:bg-transparent hover:text-minsk focus:ring-offset-0  active:bg-transparent"
                            data-test="array-item-edit-button"
                        >
                            {t('form-content.portal:linkToPortal')}
                        </DropdownMenuItem>
                    </div>
                    <div className="flex items-center justify-center px-2">
                        <Copy24 className="text-minsk" />
                        <DropdownMenuItem
                            className="hover:bg-transparent hover:text-minsk focus:ring-offset-0  active:bg-transparent"
                            onClick={() =>
                                copyToClipboard(cell.row.original.formId)
                            }
                            data-test="array-item-edit-button"
                        >
                            {t('dashboard.table-dropdown:copyFormId')}
                        </DropdownMenuItem>
                    </div>
                    <div className="flex items-center justify-center px-2">
                        <Copy24 className="text-minsk" />
                        <DropdownMenuItem
                            className="hover:bg-transparent hover:text-minsk focus:ring-offset-0  active:bg-transparent"
                            disabled={!cell.row.original.portalNoticeId}
                            onClick={() =>
                                copyToClipboard(
                                    cell.row.original.portalNoticeId
                                )
                            }
                            data-test="array-item-edit-button"
                        >
                            {t('dashboard.table-dropdown:copyPortalNoticeId')}
                        </DropdownMenuItem>
                    </div>
                    <div className="flex items-center justify-center px-2">
                        <Link24 className="text-minsk" />
                        <DropdownMenuItem
                            className="hover:bg-transparent hover:text-minsk focus:ring-offset-0  active:bg-transparent"
                            onClick={async () => {
                                const { formId } = cell.row.original;
                                const otpUrl = await getOtpUrlFromApi(
                                    formId,
                                    2,
                                    formOptions.form
                                );
                                window
                                    .open(otpUrl ?? '/login', '_blank')
                                    ?.focus();
                            }}
                            data-test="array-item-edit-button"
                        >
                            {t('dashboard.table-dropdown:openForm')}
                        </DropdownMenuItem>
                    </div>

                    <div className="flex items-center justify-center px-2">
                        <LicenseDraft24 className="text-minsk" />
                        <DropdownMenuItem
                            className="hover:bg-transparent hover:text-minsk focus:ring-offset-0  active:bg-transparent"
                            data-test="array-item-edit-button"
                            onClick={() =>
                                openModalPublicationStop(
                                    cell.row.original.formId
                                )
                            }
                            disabled={
                                !(
                                    cell.row.original.portalStatus ===
                                        publicationTaskStatusName.inProgress &&
                                    cell.row.original.status ===
                                        FormStatus.DispatchedForPublication &&
                                    (cell.row.original.portalName === 'TED' ||
                                        cell.row.original.portalName === 'DOFFIN' ||
                                        cell.row.original.portalName === 'BKMS' 
                                    )
                                )
                            }
                        >
                            {t('dashboard.table-dropdown:stopPublication')}
                        </DropdownMenuItem>
                    </div>

                    <div className="flex items-center justify-center px-2">
                        <Checkmark24 className="text-minsk" />
                        <DropdownMenuItem
                            className="hover:bg-transparent hover:text-minsk focus:ring-offset-0  active:bg-transparent"
                            onClick={() =>
                                props.viewValidationFull(cell.row.original)
                            }
                            data-test="array-item-edit-button"
                        >
                            {t('dashboard.table-dropdown:validateFormFull')}
                        </DropdownMenuItem>
                    </div>
                </DropdownMenu>
            )}
        </>
    );
};

export default TableDropDown;
